import React, { useState } from "react";
import createPlotlyComponent from "react-plotly.js/factory";
import Plotly from "plotly.js-dist-min";
import BasePlot from "./BasePlot";

const Plot = createPlotlyComponent(Plotly);

export interface ViolinPlotProps {
  data: any[];
  metric?: string;
  title?: string;
  defaultColor?: string;
  className?: string;
  plotRef?: string;
  isFullscreen?: boolean;
}

const ViolinPlot = ({
  data,
  metric = "n_genes_by_counts",
  title = "Violin Plot",
  defaultColor = "#636EFA",
  className = "",
  plotRef,
  isFullscreen = false,
}: ViolinPlotProps): React.ReactElement => {
  const [color, setColor] = useState(defaultColor);

  const metricLabel = ({
    n_genes_by_counts: "Genes detected per cell",
    total_counts: "Total counts per cell",
    pct_counts_mt: "Mitochondrial content (%)",
  } as Record<string, string>)[metric] ?? metric;

  const yData = data
    .map((d) => Number(d?.[metric]))
    .filter((value) => Number.isFinite(value));

  const plotComponent = (
    <Plot
      data={[
        {
          type: "violin",
          y: yData,
          box: { visible: true },
          points: "all",
          pointpos: 0,
          jitter: 0.2,
          marker: { color, size: 3, opacity: 0.65 },
          name: metric,
          spanmode: "soft",
        },
      ]}
      layout={{
        title: { text: "Distribution of " + metricLabel, font: { size: 14 } },
        height: isFullscreen ? undefined : 360,
        autosize: true,
        uirevision: "survom-violin",
        paper_bgcolor: "white",
        plot_bgcolor: "white",
        margin: { l: 60, r: 30, t: 40, b: 50 }, // Adequate margins to prevent label cutoff
        font: { size: 12 },
        xaxis: { showticklabels: false, zeroline: false },
        yaxis: { title: { text: metricLabel } },
      }}
      config={{
        displayModeBar: false,
        responsive: true,
      }}
      style={{ width: "100%", height: isFullscreen ? "100%" : "360px" }}
      className={plotRef ? plotRef.replace("#", "") : "plotly-plot"}
      useResizeHandler={true}
    />
  );

  return (
    <BasePlot
      title={title}
      className={className}
      showColorPicker={true}
      defaultColor={defaultColor}
      onColorChange={setColor}
      downloadFilename={`${metric}_violin`}
      plotType="violin"
      plotProps={{
        data,
        metric,
      }}
      isFullscreen={isFullscreen}
    >
      {plotComponent}
    </BasePlot>
  );
};

export default ViolinPlot;
